import type { GetEditorialConfigurationResponse, GetEditorialSearchResultsProjectResponse, GetEditorialSearchResultsResponse, GetEditorialSearchResultsStatisticsResponse } from '@/api';

export interface EditorialFaqItem {
  question: string;
  answer: string;
}

type Translate = (key: string, named?: Record<string, unknown>) => string;

// Les déploiements d'API antérieurs au champ statistics ne le renvoient pas encore
export const getStatistics = (response: GetEditorialSearchResultsResponse | null): GetEditorialSearchResultsStatisticsResponse | null => {
  const statistics = response?.statistics;
  if (!statistics || statistics.categories.length === 0) {
    return null;
  }

  return statistics;
};

export const shouldShowStatisticsSection = (configuration: GetEditorialConfigurationResponse | null, response: GetEditorialSearchResultsResponse | null): boolean => {
  if (!configuration?.statisticsSectionEnabled || !response) {
    return false;
  }

  if (response.count < configuration.statisticsSectionMinimumProjectCount) {
    return false;
  }

  return getStatistics(response) !== null;
};

// Seuls les projets payants affichent leurs prix sur le site : le tableau suit la même règle,
// dans l'ordre de pertinence éditoriale déjà appliqué par le serveur (payants en premier)
export const selectStatisticsTableProjects = (response: GetEditorialSearchResultsResponse | null, limit: number): GetEditorialSearchResultsProjectResponse[] => {
  if (!response) {
    return [];
  }

  return response.items.filter((project) => project.isPaid).slice(0, Math.max(limit, 0));
};

// Le complément de lieu est le titre amputé du préfixe formé par le titre de la catégorie, la même
// convention que la page constructeurs : il porte la bonne préposition (« à Laval », « dans les Laurentides »)
export const getLocationPhrase = (locationTitle: string | undefined, categoryTitle: string | undefined): string => {
  const title = (locationTitle ?? '').split(':')[0]?.trim() ?? '';
  const prefix = categoryTitle ?? '';
  if (!prefix || !title.toLowerCase().startsWith(prefix.toLowerCase())) {
    return '';
  }

  return title.slice(prefix.length).trim();
};

export const buildStatisticsFaqItems = (t: Translate, locationPhrase: string, statistics: GetEditorialSearchResultsStatisticsResponse): EditorialFaqItem[] => {
  const types = statistics.categories.map((category) => category.categoryName).join(', ');
  const localized = (key: string) => (locationPhrase ? t(`views.editorial.statistics.faq.${key}`, { locationPhrase }) : t(`views.editorial.statistics.faq.${key}-generic`));

  return [
    { question: localized('types.question'), answer: t('views.editorial.statistics.faq.types.answer', { types }) },
    { question: t('views.editorial.statistics.faq.warranty.question'), answer: t('views.editorial.statistics.faq.warranty.answer') },
    { question: t('views.editorial.statistics.faq.taxes.question'), answer: t('views.editorial.statistics.faq.taxes.answer') },
    { question: localized('delivery.question'), answer: t('views.editorial.statistics.faq.delivery.answer') },
  ];
};

export interface EditorialBedroomColumn {
  bedrooms: number;
  filledProjectCount: number;
}

// Prix de vente le plus bas de chaque nombre de chambres d'un projet, mesuré sur les modèles encore offerts
export const getProjectBedroomPrices = (project: GetEditorialSearchResultsProjectResponse, forSalePriceTypeId: number): Map<number, number> => {
  const prices = new Map<number, number>();

  for (const model of project.models) {
    const bedrooms = model.bedrooms;
    // Zéro chambre est une taille réelle (loft ou studio) et souvent la moins chère du projet
    if (bedrooms === null || bedrooms < 0 || model.isSold || model.price.amount <= 0 || model.price.type.id !== forSalePriceTypeId) {
      continue;
    }

    const current = prices.get(bedrooms);
    if (current === undefined || model.price.amount < current) {
      prices.set(bedrooms, model.price.amount);
    }
  }

  return prices;
};

// Une colonne par nombre de chambres ne remplace la colonne de prix unique que lorsque le tableau reste
// réellement lisible : au moins deux colonnes à comparer, et assez de cases remplies pour qu'un tiret se
// lise comme « ce projet n'offre pas cette taille » plutôt que comme une donnée manquante.
export const selectBedroomColumns = (projects: GetEditorialSearchResultsProjectResponse[], forSalePriceTypeId: number, minimumFillPercentage: number): EditorialBedroomColumn[] => {
  if (projects.length === 0) {
    return [];
  }

  const pricesByProject = projects.map((project) => getProjectBedroomPrices(project, forSalePriceTypeId));
  const filledProjectCounts = new Map<number, number>();

  for (const prices of pricesByProject) {
    for (const bedrooms of prices.keys()) {
      filledProjectCounts.set(bedrooms, (filledProjectCounts.get(bedrooms) ?? 0) + 1);
    }
  }

  // Une colonne remplie par un seul projet ne compare rien et fausserait le taux de remplissage :
  // son prix reste visible dans la colonne « à partir de », qui accompagne toujours le tableau
  const columns = [...filledProjectCounts.entries()]
    .filter(([, filledProjectCount]) => filledProjectCount >= 2)
    .map(([bedrooms, filledProjectCount]) => ({ bedrooms, filledProjectCount }))
    .sort((a, b) => a.bedrooms - b.bedrooms);

  if (columns.length < 2) {
    return [];
  }

  const projectsWithAPrice = pricesByProject.filter((prices) => prices.size > 0).length;
  const filledCells = columns.reduce((total, column) => total + column.filledProjectCount, 0);
  const fillPercentage = (filledCells / (projectsWithAPrice * columns.length)) * 100;

  return fillPercentage >= minimumFillPercentage ? columns : [];
};
